import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ConfigRow } from '../api/admin';
import { getPromptVarsForKey, insertAtCursor } from '../utils/adminPromptVars';
import { getConfigKeyHelp, getRetrievalWeightField } from '../utils/adminConfigDescriptions';
import { HelpTipButton } from './HelpTipButton';
import PromptPreviewModal from './PromptPreviewModal';
import './AdminConfigEditor.css';

/** Keys eligible for the "Previzualizează randarea" button.
 *  `prompts.*` use $-placeholders that are substituted at runtime.
 *  `responses.*` are static strings (no substitution) — the preview
 *  isn't useful there, so we hide it. */
function supportsPreview(key: string): boolean {
  return key.startsWith('prompts.');
}

function IconHelpCircle(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

interface Props {
  row: ConfigRow;
  onSave: (value: Record<string, unknown>) => void;
  onCancel: () => void;
  /** Remove this key from system_config (confirm handled by parent). */
  onDelete?: () => void;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isPromptTextShape(v: unknown): v is { text: string } {
  return isPlainObject(v) && typeof v.text === 'string';
}

function isNumericRecord(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every((x) => typeof x === 'number' && !Number.isNaN(x));
}

function isSynonymsShape(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every(
    (x) => Array.isArray(x) && x.every((y) => typeof y === 'string')
  );
}

function supportsForm(key: string, v: unknown): boolean {
  return (
    isPromptTextShape(v) ||
    (key === 'retrieval.weights' && isNumericRecord(v)) ||
    isSynonymsShape(v)
  );
}

const AdminConfigEditor: React.FC<Props> = ({ row, onSave, onCancel, onDelete }) => {
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonText, setJsonText] = useState(() => JSON.stringify(row.value, null, 2));
  const [parsed, setParsed] = useState<Record<string, unknown>>(() => ({ ...row.value }));
  const [error, setError] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const synonymListEndRef = useRef<HTMLDivElement | null>(null);
  const scrollSynonymsToEndAfterAdd = useRef(false);

  useEffect(() => {
    const v = row.value;
    setJsonText(JSON.stringify(v, null, 2));
    setParsed({ ...v });
    setError('');
    setMode(supportsForm(row.key, v) ? 'form' : 'json');
  }, [row.key, row.value]);

  const promptVars = useMemo(() => getPromptVarsForKey(row.key), [row.key]);
  const keyHelp = useMemo(() => getConfigKeyHelp(row.key), [row.key]);
  const canPreview = supportsPreview(row.key);
  const [previewOpen, setPreviewOpen] = useState(false);

  const syncJsonFromParsed = useCallback(() => {
    setJsonText(JSON.stringify(parsed, null, 2));
  }, [parsed]);

  const handleMode = (next: 'form' | 'json') => {
    setError('');
    if (next === mode) return;
    if (next === 'json') {
      syncJsonFromParsed();
      setMode('json');
      return;
    }
    try {
      const nextParsed = JSON.parse(jsonText) as Record<string, unknown>;
      setParsed(nextParsed);
      setMode('form');
    } catch {
      setError('JSON invalid — corectați înainte de a reveni la formular.');
    }
  };

  const save = () => {
    setError('');
    try {
      const value =
        mode === 'json' ? (JSON.parse(jsonText) as Record<string, unknown>) : parsed;
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        setError('Valoarea trebuie să fie un obiect JSON.');
        return;
      }
      onSave(value as Record<string, unknown>);
    } catch (e) {
      if (e instanceof SyntaxError) setError('JSON invalid.');
      else setError('Eroare la salvare.');
    }
  };

  const updatePromptText = (text: string) => {
    setParsed((prev) => ({ ...prev, text }));
  };

  const insertVar = (name: string) => {
    const t = isPromptTextShape(parsed) ? parsed.text : '';
    const token = `$${name}`;
    insertAtCursor(taRef.current, token, updatePromptText, t);
  };

  const weightKeys = useMemo(() => {
    if (row.key !== 'retrieval.weights' || !isNumericRecord(parsed)) return [];
    return Object.keys(parsed).sort();
  }, [row.key, parsed]);

  const updateWeight = (k: string, v: string) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return;
    setParsed((prev) => ({ ...prev, [k]: n }));
  };

  const synonymEntries = useMemo(() => {
    if (!isSynonymsShape(parsed)) return [];
    // Insertion order (no sort): new rows from "Adaugă rând sinonim" append at the end.
    return Object.entries(parsed);
  }, [parsed]);

  const updateSynonymKey = (oldKey: string, newKey: string) => {
    const trimmed = newKey.trim().toLowerCase();
    if (!trimmed) {
      setError('Termenul canonic nu poate fi gol.');
      return;
    }
    if (trimmed === oldKey) {
      setError('');
      return;
    }
    if (parsed[trimmed] !== undefined && trimmed !== oldKey) {
      setError('Există deja un rând cu acest termen.');
      return;
    }
    setError('');
    setParsed((prev) => {
      const p = { ...prev } as Record<string, unknown>;
      const val = p[oldKey];
      delete p[oldKey];
      p[trimmed] = val;
      return p;
    });
  };

  const updateSynonymValues = (key: string, line: string) => {
    const arr = line
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setParsed((prev) => ({ ...prev, [key]: arr }));
  };

  const addSynonymRow = () => {
    setError('');
    scrollSynonymsToEndAfterAdd.current = true;
    setParsed((prev) => {
      const p = prev as Record<string, unknown>;
      let nk = `termen_nou_${Date.now().toString(36)}`;
      let i = 0;
      while (p[nk] !== undefined) {
        nk = `termen_nou_${Date.now().toString(36)}_${i}`;
        i += 1;
      }
      return { ...p, [nk]: [] as string[] };
    });
  };

  useLayoutEffect(() => {
    if (!scrollSynonymsToEndAfterAdd.current) return;
    scrollSynonymsToEndAfterAdd.current = false;
    synonymListEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [parsed, row.key]);

  const removeSynonymKey = (key: string) => {
    setParsed((prev) => {
      const p = { ...prev } as Record<string, unknown>;
      delete p[key];
      return p;
    });
  };

  const renderFormBody = () => {
    if (isPromptTextShape(parsed)) {
      return (
        <div className="admin-editor-prompt">
          <p className="admin-editor-lead">{keyHelp.short}</p>
          <label className="admin-editor-label">
            {row.key.startsWith('responses.') ? 'Text afișat utilizatorului' : 'Conținut șablon (text)'}
          </label>
          {row.key.startsWith('responses.') ? (
            <p className="admin-editor-hint">
              Mesaj scurt din categoria <strong>responses</strong>: este folosit exact ca atare, fără substituție{' '}
              <code>$variabilă</code> (nu este <code>string.Template</code>).
            </p>
          ) : (
            <p className="admin-editor-hint">
              La rulare, backend-ul aplică Python <code>string.Template</code>: prefix <code>$</code> + nume
              câmp (ex. <code>$user_message</code>, <code>$ui_language</code>). Numele trebuie să coincidă cu
              parametrii din cod; folosiți chip-urile de mai jos pentru variabilele acceptate la această cheie.
              {promptVars.length === 0 && (
                <span> Nu există variabile predefinite în UI pentru această cheie.</span>
              )}
            </p>
          )}
          {(promptVars.length > 0 || canPreview) && (
            <div className="admin-var-chips" aria-label="Variabile disponibile">
              {promptVars.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  className="admin-var-chip"
                  title={v.hint}
                  onClick={() => insertVar(v.name)}
                >
                  <span className="admin-var-chip__sym">$</span>
                  {v.name}
                </button>
              ))}
              {canPreview && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm admin-editor-preview-btn"
                  onClick={() => setPreviewOpen(true)}
                  title="Vezi promptul cu $-placeholder-ele înlocuite (taxonomia activă pentru clasificare, valori demo pentru restul)"
                >
                  Previzualizează randarea
                </button>
              )}
            </div>
          )}
          <textarea
            ref={taRef}
            className="admin-editor-textarea admin-editor-textarea--prompt"
            value={parsed.text}
            onChange={(e) => updatePromptText(e.target.value)}
            spellCheck={false}
          />
        </div>
      );
    }

    if (row.key === 'retrieval.weights' && isNumericRecord(parsed)) {
      return (
        <div className="admin-editor-weights">
          <p className="admin-editor-lead">{keyHelp.short}</p>
          <div className="admin-weight-fields">
            {weightKeys.map((k) => {
              const meta = getRetrievalWeightField(k);
              return (
                <div key={k} className="admin-weight-card">
                  <div className="admin-weight-card__top">
                    <div className="admin-weight-card__text">
                      <div className="admin-weight-card__label-row">
                        <span className="admin-weight-card__label">{meta.label}</span>
                        <HelpTipButton
                          content={meta.long}
                          ariaLabel={`Detalii: ${meta.label}`}
                          buttonClassName="admin-weight-card__tip"
                        >
                          <IconHelpCircle />
                        </HelpTipButton>
                      </div>
                      <code className="admin-weight-card__key">{k}</code>
                      <p className="admin-weight-card__short">{meta.short}</p>
                    </div>
                  </div>
                  <input
                    type="number"
                    step="any"
                    inputMode="decimal"
                    className="input admin-weight-input"
                    value={String(parsed[k])}
                    onChange={(e) => updateWeight(k, e.target.value)}
                    aria-label={`${meta.label} (${k})`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (isSynonymsShape(parsed)) {
      return (
        <div className="admin-editor-synonyms">
          <p className="admin-editor-lead">{keyHelp.short}</p>
          <p className="admin-editor-hint">
            Fiecare rând = un <strong>termen canonic</strong> (cheie în JSON) și o listă de{' '}
            <strong>sinonime</strong> folosite la căutare. Separate prin virgulă.
          </p>
          <div className="admin-synonym-list">
            {synonymEntries.map(([key, vals], index) => {
              const rowKey = `syn-${index}-${key}`;
              const synonymsText = Array.isArray(vals) ? vals.filter((x): x is string => typeof x === 'string').join(', ') : '';
              return (
                <div key={rowKey} className="admin-synonym-card">
                  <div className="admin-synonym-card__top">
                    <span className="admin-synonym-card__index" aria-hidden>
                      {index + 1}
                    </span>
                    <div className="admin-synonym-card__fields">
                      <div className="admin-synonym-field">
                        <label className="admin-synonym-label" htmlFor={`admin-syn-term-${rowKey}`}>
                          Termen canonic
                        </label>
                        <input
                          id={`admin-syn-term-${rowKey}`}
                          key={`term-inp-${index}-${key}`}
                          type="text"
                          className="input admin-synonym-term"
                          defaultValue={key}
                          autoComplete="off"
                          spellCheck={false}
                          onBlur={(e) => updateSynonymKey(key, e.target.value)}
                        />
                        <span className="admin-synonym-micro">Salvat ca cheie JSON (minuscule)</span>
                      </div>
                      <div className="admin-synonym-field admin-synonym-field--grow">
                        <label className="admin-synonym-label" htmlFor={`admin-syn-list-${rowKey}`}>
                          Sinonime
                        </label>
                        <textarea
                          id={`admin-syn-list-${rowKey}`}
                          key={`syn-ta-${index}-${key}`}
                          className="input admin-synonym-values"
                          rows={3}
                          value={synonymsText}
                          onChange={(e) => updateSynonymValues(key, e.target.value)}
                          placeholder="Ex.: alergie, reacție alergică, hipersensibilitate"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm admin-synonym-remove"
                      onClick={() => removeSynonymKey(key)}
                      aria-label={`Șterge rândul ${key}`}
                    >
                      Șterge
                    </button>
                  </div>
                </div>
              );
            })}
            <div ref={synonymListEndRef} className="admin-synonym-list-end" aria-hidden />
          </div>
        </div>
      );
    }

    return (
      <div className="admin-editor-fallback">
        <p className="admin-editor-hint">
          Nu există formular pentru această structură. Folosiți modul <strong>JSON</strong> pentru a edita
          complet.
        </p>
      </div>
    );
  };

  const showFormContent = mode === 'form' && supportsForm(row.key, parsed);
  const showSynonymAddInFooter =
    mode === 'form' && isSynonymsShape(parsed);

  return (
    <div className="admin-editor-overlay" role="dialog" aria-modal="true" aria-labelledby="admin-editor-title">
      <div className="admin-editor-panel">
        <header className="admin-editor-header">
          <div className="admin-editor-header-main">
            <h2 id="admin-editor-title" className="admin-editor-title">
              Editare configurație
            </h2>
            <p className="admin-editor-key">
              <code>{row.key}</code>
              {row.category && <span className="admin-editor-cat">{row.category}</span>}
            </p>
          </div>
          <div className="admin-editor-header-actions">
            <HelpTipButton
              content={keyHelp.long}
              ariaLabel={`Ajutor: ${keyHelp.short}`}
              buttonClassName="admin-editor-help"
            >
              <IconHelpCircle />
            </HelpTipButton>
            <button type="button" className="admin-editor-close" onClick={onCancel} aria-label="Închide">
              ×
            </button>
          </div>
        </header>

        <div className="admin-editor-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'form'}
            className={`admin-editor-tab${mode === 'form' ? ' active' : ''}`}
            onClick={() => handleMode('form')}
          >
            Formular
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'json'}
            className={`admin-editor-tab${mode === 'json' ? ' active' : ''}`}
            onClick={() => handleMode('json')}
          >
            JSON
          </button>
        </div>

        {error && <div className="alert alert-error admin-editor-alert">{error}</div>}

        <div className="admin-editor-body">
          {mode === 'form' ? (
            showFormContent ? (
              renderFormBody()
            ) : (
              <div className="admin-editor-fallback">
                <p className="admin-editor-hint">
                  Structura curentă nu se potrivește formularului (ex: lipsește <code>text</code> pentru
                  prompt). Editați în <strong>JSON</strong> sau corectați obiectul.
                </p>
              </div>
            )
          ) : (
            <div className="admin-editor-json-wrap">
              <textarea
                className="admin-editor-textarea admin-editor-textarea--json"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                spellCheck={false}
              />
            </div>
          )}
        </div>

        <footer
          className={`admin-editor-footer${onDelete || showSynonymAddInFooter ? ' admin-editor-footer--split' : ''}`}
        >
          {(onDelete || showSynonymAddInFooter) && (
            <div className="admin-editor-footer-start">
              {onDelete && (
                <button type="button" className="btn btn-danger" onClick={onDelete}>
                  Șterge din DB
                </button>
              )}
              {showSynonymAddInFooter && (
                <button
                  type="button"
                  className="btn btn-secondary admin-synonym-add"
                  onClick={addSynonymRow}
                >
                  <span className="admin-synonym-add__icon" aria-hidden>
                    +
                  </span>
                  Adaugă rând sinonim
                </button>
              )}
            </div>
          )}
          <div className="admin-editor-footer-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Anulează
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Salvează
            </button>
          </div>
        </footer>
      </div>
      {canPreview && (
        <PromptPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          promptKey={row.key}
        />
      )}
    </div>
  );
};

export default AdminConfigEditor;
